import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Link as LinkIcon } from '@mui/icons-material';
import {
  useGetSimpleWorkoutsQuery,
  useLinkActivityToWorkoutMutation,
  useUnlinkActivityFromWorkoutMutation,
} from '../../../store/api/baseApi';

interface WorkoutLinkButtonProps {
  activityId: number;
  workoutId?: number | null;
  size?: 'small' | 'medium';
  variant?: 'icon' | 'button';
}

/**
 * WorkoutLinkButton Component
 *
 * Allows users to link/unlink workouts to activities.
 * Displays a chip with workout name if linked, or a link icon if not linked.
 */
export const WorkoutLinkButton: React.FC<WorkoutLinkButtonProps> = ({
  activityId,
  workoutId,
  size = 'small',
  variant = 'icon',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { data: workoutsData, isLoading: isLoadingWorkouts } = useGetSimpleWorkoutsQuery({});
  const [linkWorkout, { isLoading: isLinking }] = useLinkActivityToWorkoutMutation();
  const [unlinkWorkout, { isLoading: isUnlinking }] = useUnlinkActivityFromWorkoutMutation();

  // Find the current workout from the list
  const currentWorkout = workoutsData?.find(w => w.id === workoutId);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleLinkWorkout = async (selectedWorkoutId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await linkWorkout({ activityId: String(activityId), workout_id: String(selectedWorkoutId) }).unwrap();
      handleClose();
    } catch (error) {
      console.error('Failed to link workout:', error);
    }
  };

  const handleUnlinkWorkout = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await unlinkWorkout({ activityId: String(activityId) }).unwrap();
      handleClose();
    } catch (error) {
      console.error('Failed to unlink workout:', error);
    }
  };

  // If workout is linked, show chip with workout name
  if (workoutId && currentWorkout) {
    return (
      <>
        <Tooltip title="Click to change or remove workout">
          <Chip
            label={currentWorkout.name}
            size={size}
            color="success"
            variant="outlined"
            onClick={handleClick}
            sx={{ cursor: 'pointer' }}
            disabled={isUnlinking}
          />
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => handleClose()}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem onClick={handleUnlinkWorkout} sx={{ color: 'error.main' }}>
            Remove Workout Link
          </MenuItem>
          <MenuItem disabled>─────────────</MenuItem>
          {isLoadingWorkouts ? (
            <MenuItem disabled>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Loading workouts...
            </MenuItem>
          ) : workoutsData && workoutsData.length > 0 ? (
            workoutsData.map((workout) => (
              <MenuItem
                key={workout.id}
                onClick={(e) => handleLinkWorkout(workout.id, e)}
                selected={workout.id === workoutId}
              >
                {workout.name}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No workouts available</MenuItem>
          )}
        </Menu>
      </>
    );
  }

  // If no workout linked, show link icon or button based on variant
  return (
    <>
      <Tooltip title="Link to workout">
        <IconButton
          size={size}
          onClick={handleClick}
          disabled={isLinking}
          sx={{ color: 'action.active' }}
        >
          <LinkIcon fontSize={size} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleClose()}
        onClick={(e) => e.stopPropagation()}
      >
        {isLoadingWorkouts ? (
          <MenuItem disabled>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Loading workouts...
          </MenuItem>
        ) : workoutsData && workoutsData.length > 0 ? (
          workoutsData.map((workout) => (
            <MenuItem
              key={workout.id}
              onClick={(e) => handleLinkWorkout(workout.id, e)}
            >
              {workout.name}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No workouts available</MenuItem>
        )}
      </Menu>
    </>
  );
};
